/**
 * The avatar buttons of an avatars() quick-filter row. A mouse toggle drops
 * the focus (the row is meant to be clicked through, and a focused avatar
 * would read as "active"); a keyboard toggle keeps it so Space/Enter can go
 * on. Selection is presented immediately for the existing wire:click action,
 * then reconciled to the server's markup when that action finishes. No filter
 * state or request is written here. Before the next paint after the last
 * pending action finishes, recover keyboard focus if morphing lost it;
 * leave focus alone when the user moved to another control.
 */
export default () => ({
    pendingFocus: null,
    _focusOrigin: null,
    _cleanups: [],
    _serverSelection: new Map(),
    _pending: [],
    _nextToggle: 0,
    _finishFrames: [],
    _destroyed: false,

    init() {
        this.readSelection(this.$root);
        const focusMoved = event => {
            if (this.pendingFocus !== null && event.target !== this._focusOrigin) {
                this.pendingFocus = null;
                this._focusOrigin = null;
            }
        };
        document.addEventListener('focusin', focusMoved);
        this._cleanups.push(() => document.removeEventListener('focusin', focusMoved));

        if (window.Livewire?.hook) {
            this._cleanups.push(window.Livewire.hook('morphed', ({ component } = {}) => {
                if (!component || component.el?.contains(this.$root)) this.restoreFocus();
            }));
            this._cleanups.push(window.Livewire.hook('morph.updating', ({ el, toEl }) => {
                if (el !== this.$root) return;

                // Unkeyed custom toolbars can reuse this root for another filter.
                // Value matches alone do not carry selection or focus across rows.
                if (toEl.dataset.quickFilter !== el.dataset.quickFilter) {
                    this._pending = [];
                    this.pendingFocus = null;
                    this._focusOrigin = null;
                }

                // Read the untouched server echo before Alpine clones/patches attributes.
                // An earlier or unrelated response must not erase later pending choices.
                this.readSelection(toEl);
                this.paintSelection(toEl);
            }));
        }

        if (window.Livewire?.interceptAction) {
            const sentCalls = new WeakSet();
            this._cleanups.push(window.Livewire.interceptAction(({ action, onSend, onFinish }) => {
                const button = action.origin?.el;
                if (action.isCancelled?.() || !button?.hasAttribute('data-quick-filter-avatar') || !this.$root?.contains(button)) return;

                const id = ++this._nextToggle;
                const deferred = action.isDeferred?.() === true;
                this._pending.push({
                    id,
                    value: button.dataset.quickFilterAvatar,
                    active: button.getAttribute('aria-pressed') !== 'true',
                    fingerprint: action.fingerprint,
                    deferred,
                    sent: false,
                    // Livewire defers behind an active message, then fires that queue
                    // together. Identical fingerprints will be one call, not two toggles.
                    duplicate: deferred && this._pending.some(toggle => toggle.deferred
                        && !toggle.sent && toggle.fingerprint === action.fingerprint),
                });
                this.paintSelection(this.$root);

                // The network coordinator can register after Alpine initializes
                // this row. Observe its deferral decision after all interceptors,
                // still before the browser paints the queued clicks.
                queueMicrotask(() => {
                    const toggle = this._pending.find(toggle => toggle.id === id);
                    if (!toggle || toggle.sent || this._destroyed) return;
                    toggle.deferred = action.isDeferred?.() === true;
                    toggle.fingerprint = action.fingerprint;
                    toggle.duplicate = toggle.deferred && this._pending.some(earlier => earlier.id < id
                        && earlier.deferred && !earlier.sent && earlier.fingerprint === toggle.fingerprint);
                    this.paintSelection(this.$root);
                });

                onSend?.(({ call }) => {
                    const toggle = this._pending.find(toggle => toggle.id === id);
                    if (!toggle || this._destroyed) return;
                    // Squashed actions receive the exact same call object (4.4.7),
                    // including clicks bundled before any message became active.
                    toggle.sent = true;
                    toggle.duplicate = sentCalls.has(call);
                    sentCalls.add(call);
                    this.paintSelection(this.$root);
                });

                // 4.4.7 finishes after morph, early Livewire 4 before morph. Settle
                // before the next paint so either ordering captures the server echo
                // and restores focus after replacement. Failure/cancellation without
                // an echo uses the last rendered server selection. Squashed calls
                // each finish, so each retires its own presentation intent.
                onFinish(() => {
                    if (this._destroyed || !this._pending.some(toggle => toggle.id === id)) return;
                    const frame = requestAnimationFrame(() => {
                        this._finishFrames = this._finishFrames.filter(value => value !== frame);
                        // A root identity change can also retire an intent after
                        // its finish frame was scheduled but before it is painted.
                        if (this._destroyed || !this._pending.some(toggle => toggle.id === id)) return;
                        this._pending = this._pending.filter(toggle => toggle.id !== id);
                        this.paintSelection(this.$root);
                        this.restoreFocus();
                    });
                    this._finishFrames.push(frame);
                });
            }));
        }
    },

    destroy() {
        this._destroyed = true;
        this._cleanups.forEach(cleanup => cleanup?.());
        this._cleanups = [];
        this._finishFrames.forEach(frame => cancelAnimationFrame(frame));
        this._finishFrames = [];
        this._pending = [];
        this.pendingFocus = null;
        this._focusOrigin = null;
    },

    buttons(root) {
        return [...(root?.querySelectorAll?.('[data-quick-filter-avatar], [data-quick-filter-avatar-more]') ?? [])];
    },

    readSelection(root) {
        this._serverSelection = new Map(this.buttons(root).map(button => [
            button.dataset.quickFilterAvatar ?? null,
            button.getAttribute('aria-pressed') === 'true',
        ]));
    },

    paintSelection(root) {
        const selection = new Map(this._serverSelection);

        for (const toggle of this._pending) {
            if (toggle.duplicate) continue;
            if (this.$root?.dataset?.avatarRowSingle === 'true') {
                // Clearing/replacing a single choice also clears a hidden choice's +n ring.
                for (const value of selection.keys()) selection.set(value, false);
            }
            selection.set(toggle.value, toggle.active);
        }

        for (const button of this.buttons(root)) {
            const active = selection.get(button.dataset.quickFilterAvatar ?? null) === true;
            button.classList.toggle('is-active', active);
            button.setAttribute('aria-pressed', String(active));
            if (button.hasAttribute('data-quick-filter-avatar-more')) {
                button.setAttribute('data-quick-filter-active', String(active));
            }
        }
    },

    /** event.detail is 0 for keyboard-initiated clicks, the click count otherwise. */
    toggled(event, button) {
        if (event.detail > 0) {
            button.blur?.();
            this.pendingFocus = null;
            this._focusOrigin = null;

            return;
        }

        this.pendingFocus = button.dataset?.quickFilterAvatar ?? null;
        this._focusOrigin = button;
    },

    restoreFocus() {
        if (this.pendingFocus === null || this._pending.length > 0) return;

        const value = this.pendingFocus;
        this.pendingFocus = null;
        this._focusOrigin = null;

        // A disconnected focused button normally leaves focus on body. Never
        // replace a deliberate focus choice that survived the response.
        if (document.activeElement !== document.body && document.activeElement?.isConnected) return;

        const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value;
        // Should the button be gone (a row re-rendered with fewer options),
        // the "+n" button is the place to continue from.
        const button = this.$root?.querySelector?.(`[data-quick-filter-avatar="${escaped}"]`)
            || this.$root?.querySelector?.('[data-quick-filter-avatar-more]');

        if (button && document.activeElement !== button) {
            button.focus();
        }
    },
})
